import { cn } from '@utils/shad-utils.ts'
import { X } from 'lucide-react'
import * as React from 'react'

interface ChipProps {
  label: string
  onRemove: () => void
  disabled?: boolean
  className?: string
}

export const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  ({ label, onRemove, disabled = false, className, ...props }, ref) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return

      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault()
        e.stopPropagation()
        onRemove()
      }
    }

    const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (!disabled) {
        onRemove()
      }
    }

    return (
      <div
        ref={ref}
        data-slot="chip"
        role="option"
        tabIndex={disabled ? -1 : 0}
        aria-label={`${label}, press Backspace or Delete to remove`}
        className={cn(
          'inline-flex items-center gap-1 rounded-md border border-input bg-secondary text-secondary-foreground px-1.5 py-0.5 text-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
        onKeyDown={handleKeyDown}
        {...props}
      >
        <span className="whitespace-nowrap">{label}</span>
        <button
          type="button"
          onClick={handleRemoveClick}
          disabled={disabled}
          aria-label={`Remove ${label}`}
          className={cn(
            'ml-0.5 rounded-sm p-0.5 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    )
  },
)

Chip.displayName = 'Chip'
